import { createContext, useContext, useEffect, useCallback, useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useLocation } from 'react-router-dom'
import { InitialType, useUrlSearchParams } from 'use-url-search-params'

const CACHED_PARAMS = new Map<string, InitialType>()

export type ParamKey = 'fieldId' | 'property' | 'filter' | 'sort'

interface State {
  params: InitialType
}

interface ContextValue extends State {
  updateParams: (nextParams: InitialType) => void
  updateCache: (nextParam: InitialType) => void
  resetCache: () => void
}

const SearchParamsContext = createContext<ContextValue | null>(null)

export const SearchParamsContextProvider = ({ children }) => {
  const { pathname } = useLocation()
  const [params, setParams] = useUrlSearchParams({}, undefined, true)

  useEffect(() => {
    const cached = CACHED_PARAMS.get(pathname)

    if (!cached) return

    setParams(cached)
  }, [pathname])

  const updateParams = useCallback(
    function updateParams(nextParams: InitialType) {
      setParams({
        ...params,
        ...nextParams,
      })
    },
    [params],
  )

  const resetCache = useCallback(
    function resetCache() {
      CACHED_PARAMS.delete(pathname)
    },
    [pathname],
  )

  const updateCache = useCallback(
    function updateCache(newParams: InitialType) {
      if (isEmpty(newParams)) {
        CACHED_PARAMS.delete(pathname)
      } else {
        CACHED_PARAMS.set(pathname, newParams)
      }
    },
    [pathname],
  )

  const value = useMemo(
    () => ({
      params,
      updateParams,
      updateCache,
      resetCache,
    }),
    [params, updateParams, updateCache, resetCache],
  )

  return <SearchParamsContext.Provider value={value}>{children}</SearchParamsContext.Provider>
}

export function useSearchParamsContext(): ContextValue {
  const context = useContext(SearchParamsContext)

  if (!context) {
    throw new Error(
      `${useSearchParamsContext.name} must be used within a ${SearchParamsContextProvider.name}`,
    )
  }

  return context
}
